import { useLayoutEffect, useRef } from 'react';
import { TreeCard } from './TreeCard';
import type { TreeProperties } from '../lib/types';

interface Props {
  properties: TreeProperties;
  x: number;
  y: number;
  selected: boolean;
}

/** Gap between the cursor and the card, and between the card and any edge. */
const OFFSET = 18;
const MARGIN = 12;

/**
 * The pointer-driven tree card: follows the cursor, hover only.
 *
 * Placement used to be `Math.min(x + 18, innerWidth - 250)` with a matching
 * `innerHeight - 330` for the vertical -- two constants standing in for the
 * card's own width and for the height of the solar control docked along the
 * bottom. Both were wrong on any window the numbers were not chosen against:
 * on a short window the vertical clamp went negative and parked the card off
 * the top of the screen. This measures the card and the control instead, and
 * flips to the other side of the cursor when that is the only way to fit.
 */
export function TreeTooltip({ properties, x, y, selected }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // No dependency array: this runs on every pointer move, which is exactly the
  // cadence the card needs to be repositioned at. Writing the transform to the
  // node rather than to state keeps that off React's render path.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const { offsetWidth: w, offsetHeight: h } = el;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // The solar control owns the bottom of the screen. Measured rather than
    // assumed, because its height changes with the breakpoint.
    const solar = document.querySelector('.solar');
    const floor = solar ? solar.getBoundingClientRect().top - MARGIN : vh - MARGIN;

    let left = x + OFFSET;
    if (left + w > vw - MARGIN) left = x - OFFSET - w;
    left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));

    let top = y + OFFSET;
    if (top + h > floor) top = y - OFFSET - h;
    top = Math.max(MARGIN, Math.min(top, Math.max(MARGIN, floor - h)));

    el.style.transform = `translate3d(${left}px, ${top}px, 0)`;
  });

  return (
    <div
      ref={ref}
      className={`tip tip--float${properties.has_canopy === 0 ? ' tip--warning' : ''}`}
    >
      <TreeCard
        properties={properties}
        hint={selected ? 'Selected — click to deselect' : 'Click to isolate this tree'}
      />
    </div>
  );
}
